import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Save, ChevronLeft, ChevronRight, Tag, Package, Check, Layers, X } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

const LEVELS = [1, 2, 3, 4];

export default function ProductPrices() {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState({});
  const [hsnEdit, setHsnEdit] = useState({});
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    api.get("/sales/business-settings").then((r) => setSettings(r.data || {})).catch(() => setSettings({}));
  }, []);

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      const r = await api.get("/sales/product-prices", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setDirty({}); // reset unsaved changes on reload
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const levelNames = useMemo(() => ({
    1: settings?.price_level_1_name || "MRP",
    2: settings?.price_level_2_name || "Retail",
    3: settings?.price_level_3_name || "Wholesale",
    4: settings?.price_level_4_name || "Special",
  }), [settings]);

  const setPrice = (pid, level, val) => {
    const cleaned = val === "" ? "" : val.replace(/[^0-9.]/g, "");
    setDirty((d) => ({
      ...d,
      [pid]: { ...(d[pid] || {}), [`price_level_${level}`]: cleaned },
    }));
    setItems((arr) => arr.map((r) => r.product_id === pid ? { ...r, [`price_level_${level}`]: cleaned === "" ? null : Number(cleaned) } : r));
  };

  const dirtyCount = Object.keys(dirty).length;

  const saveHsn = async (pid, value) => {
    try {
      await api.patch(`/sales/products/${pid}/hsn`, { hsn_code: value || null });
      setItems((arr) => arr.map((r) => r.product_id === pid ? { ...r, hsn_code: value || null } : r));
      setHsnEdit((s) => { const c = { ...s }; delete c[pid]; return c; });
      toast.success("HSN saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "HSN save failed"); }
  };

  const saveAll = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    let ok = 0, fail = 0;
    try {
      for (const pid of Object.keys(dirty)) {
        const body = dirty[pid];
        const payload = {};
        for (const k of ["price_level_1", "price_level_2", "price_level_3", "price_level_4"]) {
          if (k in body) payload[k] = body[k] === "" ? null : Number(body[k]);
        }
        try {
          await api.put(`/sales/product-prices/${pid}`, payload);
          ok += 1;
        } catch { fail += 1; }
      }
      if (fail === 0) toast.success(`Saved ${ok} product${ok === 1 ? "" : "s"}`);
      else toast.error(`${ok} saved · ${fail} failed`);
      setDirty({});
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <PageSection
        title="Multi-Level Pricing"
        description="Assign up to 4 selling prices per product. Prices left blank fall back to the product's default price for that level during invoicing."
        action={
          <div className="flex gap-2">
            <Button variant="outline" className="gap-1.5" onClick={() => setBulkOpen(true)} data-testid="pp-bulk-hsn-btn">
              <Layers className="h-4 w-4" /> Set HSN by Category
            </Button>
            <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={saveAll} disabled={saving || dirtyCount === 0} data-testid="pp-save-all">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}` : "No changes"}
            </Button>
          </div>
        }
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-md min-w-[240px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / SKU / code…" className="pl-8" data-testid="pp-search" />
            </div>
            <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
              <Tag className="h-3.5 w-3.5" />
              <span>Level names configurable in <span className="font-medium text-gray-700">Business Settings</span></span>
            </div>
            <span className="text-xs text-gray-500">{total.toLocaleString()} product(s)</span>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">HSN</th>
                  <th className="px-3 py-2 text-right">Sale Price</th>
                  <th className="px-3 py-2 text-right">MRP</th>
                  <th className="px-3 py-2 text-right">Default</th>
                  {LEVELS.map((lv) => (
                    <th key={lv} className="px-3 py-2 text-right">
                      L{lv} · <span className="text-gray-700 normal-case">{levelNames[lv]}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-10 text-center text-gray-400">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-30" /> No products.
                  </td></tr>
                )}
                {items.map((p) => (
                  <tr key={p.product_id} className={`hover:bg-gray-50 ${dirty[p.product_id] ? "bg-amber-50/40" : ""}`} data-testid={`pp-row-${p.product_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.product_name}</div>
                      <div className="text-[11px] font-mono text-gray-500">{p.product_sku || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{p.category || "—"}</td>
                    <td className="px-3 py-2">
                      {hsnEdit[p.product_id] !== undefined ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={hsnEdit[p.product_id]}
                            onChange={(e) => setHsnEdit((s) => ({ ...s, [p.product_id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") saveHsn(p.product_id, hsnEdit[p.product_id]); if (e.key === "Escape") setHsnEdit((s) => { const c = { ...s }; delete c[p.product_id]; return c; }); }}
                            className="h-7 w-20 rounded border border-gray-300 px-2 text-xs font-mono"
                            autoFocus
                            data-testid={`pp-hsn-input-${p.product_id}`}
                          />
                          <button onClick={() => saveHsn(p.product_id, hsnEdit[p.product_id])} className="h-7 w-7 rounded bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center" data-testid={`pp-hsn-save-${p.product_id}`}><Check className="h-3.5 w-3.5 text-emerald-700" /></button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setHsnEdit((s) => ({ ...s, [p.product_id]: p.hsn_code || "" }))}
                          className="font-mono text-xs text-left hover:bg-blue-50 rounded px-2 py-0.5 border border-transparent hover:border-blue-200 min-w-[54px]"
                          data-testid={`pp-hsn-edit-${p.product_id}`}
                        >
                          {p.hsn_code || <span className="text-gray-400 italic">set</span>}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums text-gray-600" data-testid={`pp-sale-price-${p.product_id}`}>
                      {p.sale_price != null ? Number(p.sale_price).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums text-gray-600" data-testid={`pp-mrp-${p.product_id}`}>
                      {p.mrp != null ? Number(p.mrp).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums text-gray-600">
                      {p.default_price != null ? Number(p.default_price).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}
                    </td>
                    {LEVELS.map((lv) => (
                      <td key={lv} className="px-3 py-2 text-right">
                        <Input
                          type="text"
                          inputMode="decimal"
                          className="h-8 w-24 text-right tabular-nums font-mono ml-auto"
                          value={p[`price_level_${lv}`] ?? ""}
                          onChange={(e) => setPrice(p.product_id, lv, e.target.value)}
                          placeholder="—"
                          data-testid={`pp-price-${p.product_id}-l${lv}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Surface>
        {totalPages > 1 && (
          <div className="mt-3 bg-white border border-gray-200 rounded-md px-4 py-2.5 flex items-center gap-2 text-xs" data-testid="pp-pagination">
            <span>Page <span className="font-semibold">{page}</span> of {totalPages}</span>
            {dirtyCount > 0 && <span className="text-amber-700 ml-3">⚠ {dirtyCount} unsaved change(s) — save before switching pages.</span>}
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(1)}>« First</Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last »</Button>
            </div>
          </div>
        )}
      </PageSection>
      {bulkOpen && <BulkHsnModal onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
    </div>
  );
}

function BulkHsnModal({ onClose, onDone }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [hsn, setHsn] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/production/product-taxes/categories").then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!category) { setPreview(null); return; }
    api.get("/sales/product-prices", { params: { category, page: 1, page_size: 1 } })
      .then((r) => setPreview({ total: r.data.total || 0 }))
      .catch(() => setPreview(null));
  }, [category]);

  const save = async () => {
    if (!category) return toast.error("Pick a category");
    if (!hsn.trim()) return toast.error("Enter an HSN code");
    if (!window.confirm(`Set HSN "${hsn.trim()}" on ALL ${preview?.total ?? "?"} products in category "${category}"? This will overwrite any existing HSN.`)) return;
    setSaving(true);
    try {
      const r = await api.post("/sales/products/hsn/bulk-set-by-category", { category, hsn_code: hsn.trim() });
      toast.success(`Updated ${r.data.updated} product${r.data.updated === 1 ? "" : "s"} with HSN ${r.data.hsn_code}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="pp-bulk-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 flex items-center gap-3">
          <Layers className="h-5 w-5 text-[#9B1C1C]" />
          <div>
            <h3 className="font-heading font-bold text-gray-900">Set HSN by Category</h3>
            <div className="text-xs text-gray-500 mt-0.5">Assign one HSN to every product inside a category in one shot.</div>
          </div>
          <button onClick={onClose} className="ml-auto h-8 w-8 rounded hover:bg-gray-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block text-sm">
            <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">Category *</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 w-full rounded-md border border-gray-200 text-sm px-2" data-testid="pp-bulk-category">
              <option value="">— Pick a category —</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {preview && (
              <span className="text-[11px] text-gray-600 mt-1 block">
                {preview.total.toLocaleString()} product{preview.total === 1 ? "" : "s"} will be updated.
              </span>
            )}
          </label>
          <label className="block text-sm">
            <span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">HSN Code *</span>
            <Input value={hsn} onChange={(e) => setHsn(e.target.value)} placeholder="e.g. 7117" className="font-mono" data-testid="pp-bulk-hsn" />
          </label>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800">
            ⚠ This overwrites any existing HSN on all matching products. Products with a custom HSN in this category will lose it.
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 flex items-center gap-2">
          <Button variant="outline" className="ml-auto" onClick={onClose} data-testid="pp-bulk-cancel">Close</Button>
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={save} disabled={saving || !category || !hsn.trim()} data-testid="pp-bulk-save">
            <Check className="h-4 w-4" /> {saving ? "Applying…" : "Apply to Category"}
          </Button>
        </div>
      </div>
    </div>
  );
}
